import { isComplianceSubdomain } from '../utils/getSubdomain';
import dayjs from 'dayjs';
import {
  BoxType,
  BulkShipmentType as BulkShipmentPayloadType,
  ProductType,
  ShipmentByIdType,
  ShipperConsigneeIdType,
  UserType,
} from '../store/reducers/Shipment/Type';

import {
  BulkShipmentType,
  PackageType,
  PickupDataType,
  ShipmentDataType,
  userDetailsType,
  GetShipmentPayloadType,
  BoxType as BoxTypePayload,
} from '../types/shipment';
import { resolveShipmentValueInEur, getEuComplianceRule, isEUCountry } from '../utils/euCompliance';
import {
  isUSDestination,
  buildBackendCpscProductPayload,
  normalizeCpscDisclaimCode,
  parseBoolean,
} from '../utils/usCompliance';
import { UsCpscComplianceType } from '../types/shipment';

export function getAddressObj(address: userDetailsType) {
  const { altContactName, altContactNo, altContactTelephoneCode } = address;
  const obj: UserType = {
    companyName: address.companyName,
    line1: address.line1,
    line2: address.line2,
    city: address.city,
    state: address.state,
    country: address.country,
    pincode: address.pincode,
    name: address.name,
    email: address.email,
    phone: {
      telephoneCode: address.telephoneCode,
      number: address.phoneNumber,
    },
  };
  if (altContactName && altContactNo && altContactTelephoneCode) {
    obj.alternatePhone = {
      telephoneCode: address.altContactTelephoneCode || '',
      number: address.altContactNo || '',
      name: address.altContactName || '',
    };
  }
  return obj;
}

// export function getSingleShipmentPackagePayload({
//   boxes,
//   packageData,
//   shipmentType,
//   pickupType,
// }: {
//   boxes: BoxType[];
//   packageData: PackageType;
//   shipmentType: ShipmentDataType;
//   pickupType: PickupDataType;
// }): PackageType {
//   console.info({ boxes });
//   const boxesTemp: BoxType[] = [...boxes];
//   const packageObj: PackageType = {
//     ...packageData,
//     unitOfMeasurement: shipmentType.unit,
//     currency: shipmentType.currency,
//     shipmentMode: shipmentType?.shipmentMode,
//     invoiceNumber: shipmentType.invoiceNumber || '',
//     userReferenceNumber: shipmentType.referenceNumber || '',
//     isDocument: shipmentType.type === 'document',
//   };
//   if (pickupType?.type === 'pickUp') {
//     packageObj.timeSlot = pickupType.timeSlot;
//     packageObj.pickupDate = pickupType.date ? `${pickupType.date}` : null;
//     packageObj.isDropOff = false;
//   }
//   const packageBoxes = boxesTemp.map(item => {
//     const products = item.products.map((product: ProductType) => {
//       const copyProduct: ProductType = { ...product };
//       delete copyProduct.actions;
//       delete copyProduct.id;
//       delete copyProduct.totalValue;
//       // product.value = product.unitValue;
//       return product;
//     });
//     // delete item.id;
//     delete item.selected;
//     delete item.startCount;
//     return <BoxTypePayload>{
//       ...item,
//       products,
//     };
//   });
//   packageObj.box = packageBoxes;
//   return packageObj;
// }

export function getSingleShipmentPackagePayload({
  boxes,
  packageData,
  shipmentType,
  pickupType,
  destinationCountry,
  extraDetails,
}: {
  boxes: BoxType[];
  packageData: PackageType;
  shipmentType: ShipmentDataType;
  pickupType: PickupDataType;
  destinationCountry?: string;
  extraDetails?: any;
}): PackageType {
  const boxesTemp: BoxType[] = [...boxes];

  const packageObj: PackageType = {
    ...packageData,
    unitOfMeasurement: shipmentType.unit,
    currency: shipmentType.currency,
    shipmentMode: shipmentType?.shipmentMode,
    invoiceNumber: shipmentType.invoiceNumber || '',
    isDocument: shipmentType.type === 'document',
    incoterm: shipmentType?.incoterm || packageData?.incoterm || 'FOB',
    freight:
      shipmentType?.freight !== undefined
        ? Number(shipmentType.freight)
        : packageData?.freight !== undefined
        ? Number(packageData.freight)
        : 0,
    freightInsurance:
      shipmentType?.freightInsurance !== undefined
        ? Number(shipmentType.freightInsurance)
        : packageData?.freightInsurance !== undefined
        ? Number(packageData.freightInsurance)
        : 0,
    iossNumber: shipmentType?.iossNumber
      ? String(shipmentType.iossNumber).trim()
      : packageData?.iossNumber
      ? String(packageData.iossNumber).trim()
      : '',
    iorEoriNumber: shipmentType?.iorEoriNumber
      ? String(shipmentType.iorEoriNumber).trim()
      : packageData?.iorEoriNumber
      ? String(packageData.iorEoriNumber).trim()
      : '',
  };

  delete (packageObj as any).userReferenceNumber;
  delete (packageObj as any).shipmentMode;
  delete (packageObj as any).recipientVatTaxId;

  if (pickupType?.type === 'pickUp') {
    packageObj.timeSlot = pickupType.timeSlot;
    packageObj.pickupDate = pickupType.date ? `${pickupType.date}` : null;
    packageObj.isDropOff = false;
  }

  const isComplianceActive = isComplianceSubdomain(extraDetails?.customHostname);
  const isDocument = shipmentType?.type === 'document' || packageData?.isDocument === true;
  const isUS = isComplianceActive && !isDocument && isUSDestination(destinationCountry);
  const isEU = isComplianceActive && isEUCountry(destinationCountry);
  const isEUOrUS = isComplianceActive && (isEU || isUS);
  const currentMode = (shipmentType?.shipmentMode || packageData?.shipmentMode || '').trim().toLowerCase();
  const rawPurpose =
    extraDetails?.purposeOfShipment ||
    (packageData as any)?.purposeOfShipment ||
    (shipmentType as any)?.purposeOfShipment ||
    '';
  const euRule = getEuComplianceRule({
    destCountry: destinationCountry,
    shipmentMode: currentMode,
    shipmentValueEur: resolveShipmentValueInEur(boxesTemp, shipmentType?.currency || packageData?.currency || 'INR'),
    purposeOfShipment: rawPurpose,
    isDocument,
    customHostname: extraDetails?.customHostname,
  });
  const isEUComplianceVisible = euRule.showEuComplianceFields;

  const packageBoxes = boxesTemp.map(item => {
    const length = item.length === 0 ? null : item.length;
    const width = item.width === 0 ? null : item.width;
    const height = item.height === 0 ? null : item.height;

    const products = item.products.map((product: ProductType) => {
      const rawCpsc = product.cpscCompliance || (product as any).compliance?.us;
      const cpscBackendPayload = isUS
        ? buildBackendCpscProductPayload(rawCpsc, true, extraDetails?.customHostname)
        : undefined;

      const copyProduct: ProductType = {
        ...product,
      };

      if (!isDocument && isEUOrUS) {
        copyProduct.productId = product.productId ? String(product.productId).trim() : '';
        copyProduct.productIdType = product.productIdType ? String(product.productIdType).trim() : 'SKU';
      } else {
        delete copyProduct.productId;
        delete copyProduct.productIdType;
      }

      if (isEUComplianceVisible) {
        const standardManufacturerProductId =
          product.standardManufacturerProductId && String(product.standardManufacturerProductId).trim() !== ''
            ? String(product.standardManufacturerProductId).trim()
            : 'NO';
        copyProduct.merchantProductId = product.merchantProductId ? String(product.merchantProductId).trim() : '';
        copyProduct.standardManufacturerProductId = standardManufacturerProductId;
        copyProduct.nonStandardManufacturerProductId = product.nonStandardManufacturerProductId
          ? String(product.nonStandardManufacturerProductId).trim()
          : '';
      } else {
        delete copyProduct.merchantProductId;
        delete copyProduct.standardManufacturerProductId;
        delete copyProduct.nonStandardManufacturerProductId;
      }

      // Defensively strip previous US CPSC backend fields before attaching active cpscBackendPayload
      delete copyProduct.isRegulated;
      delete copyProduct.isRegistered;
      delete copyProduct.disclaimMessageSet;
      delete copyProduct.referenceMessageSet;

      if (cpscBackendPayload) {
        Object.assign(copyProduct, cpscBackendPayload);
      }

      delete copyProduct.cpscCompliance;
      delete (copyProduct as any).compliance;
      delete copyProduct.actions;
      delete copyProduct.id;
      delete copyProduct.totalValue;
      return copyProduct;
    });

    delete item.selected;
    delete item.startCount;

    return <BoxTypePayload>{
      ...item,
      length,
      width,
      height,
      products,
    };
  });

  packageObj.box = packageBoxes;
  packageObj.purposeOfShipment = rawPurpose || 'FOR_SALE';
  if (extraDetails) {
    (packageObj as any).extraDetails = extraDetails;
  }
  return packageObj;
}

export function getShipmentPayload({
  shipments,
  isDropOff,
  pickupDate,
  timeSlot,
  warehouseId,
  warehouseAddress,
}: GetShipmentPayloadType) {
  const payload: BulkShipmentPayloadType[] = [];
  shipments.map((shipment: BulkShipmentType) => {
    const destCountry = ((shipment.consignee?.country as any)?.name || shipment.consignee?.country || '') as string;
    const currency = shipment.package?.currency || 'INR';
    const totalShipmentVal = shipment.package?.totalShipmentValue;
    const boxesTemp: BoxType[] = [...shipment.package.box];
    const shipmentValueEur = resolveShipmentValueInEur(boxesTemp, currency, totalShipmentVal);
    const shipmentMode = shipment.package?.shipmentMode || (shipment as any)?.shipmentMode || '';
    const isDocument = shipment.package?.isDocument === true || (shipment as any)?.type === 'document';
    const rawPurpose = (shipment.package as any)?.purposeOfShipment || (shipment as any)?.purposeOfShipment || '';
    const euRule = getEuComplianceRule({
      destCountry,
      shipmentMode,
      shipmentValueEur,
      purposeOfShipment: rawPurpose,
      isDocument,
    });

    const isComplianceActive = isComplianceSubdomain();
    const isUS = isComplianceActive && !isDocument && isUSDestination(destCountry);
    const isEU = isComplianceActive && isEUCountry(destCountry);
    const isEUOrUS = isComplianceActive && (isEU || isUS);

    shipment.package.box = boxesTemp.map(item => {
      const products = item.products.map((product: ProductType) => {
        delete product.actions;
        delete product.id;
        delete product.totalValue;
        product.value = product.unitValue;

        if (!isDocument && isEUOrUS) {
          product.productId = product.productId ? String(product.productId).trim() : '';
          product.productIdType = product.productIdType ? String(product.productIdType).trim() : 'SKU';
        } else {
          delete product.productId;
          delete product.productIdType;
        }

        if (euRule.showEuComplianceFields) {
          let standardManufacturerProductId = product.standardManufacturerProductId
            ? String(product.standardManufacturerProductId).trim()
            : '';
          standardManufacturerProductId = standardManufacturerProductId !== '' ? standardManufacturerProductId : 'NO';
          product.merchantProductId = product.merchantProductId ? String(product.merchantProductId).trim() : '';
          product.standardManufacturerProductId = standardManufacturerProductId;
          product.nonStandardManufacturerProductId = product.nonStandardManufacturerProductId
            ? String(product.nonStandardManufacturerProductId).trim()
            : '';
        } else {
          delete product.merchantProductId;
          delete product.standardManufacturerProductId;
          delete product.nonStandardManufacturerProductId;
        }

        const rawCpsc = product.cpscCompliance || (product as any).compliance?.us;
        const cpscBackendPayload = isUS && rawCpsc ? buildBackendCpscProductPayload(rawCpsc, true) : undefined;
        delete product.isRegulated;
        delete product.isRegistered;
        delete product.disclaimMessageSet;
        delete product.referenceMessageSet;

        if (cpscBackendPayload) {
          Object.assign(product, cpscBackendPayload);
        }

        delete product.cpscCompliance;
        delete (product as any).compliance;

        return product;
      });
      // delete item.id;
      delete item.selected;
      delete item.startCount;
      return <BoxTypePayload>{
        ...item,
        products,
      };
    });
    const rawDutyType =
      typeof shipment.dutyCharge === 'object'
        ? shipment.dutyCharge?.type
        : typeof (shipment.package as any)?.dutyCharge === 'object'
        ? (shipment.package as any)?.dutyCharge?.type
        : shipment.dutyCharge || (shipment.package as any)?.dutyCharge;
    const dutyType = rawDutyType ? String(rawDutyType).trim() : '';

    const formattedDutyCharge = dutyType
      ? {
          type: dutyType,
          charge:
            typeof shipment.dutyCharge === 'object'
              ? Number(shipment.dutyCharge.charge) || 0
              : typeof (shipment.package as any)?.dutyCharge === 'object'
              ? Number((shipment.package as any)?.dutyCharge?.charge) || 0
              : 0,
        }
      : undefined;

    const packageWithNewFields = {
      ...shipment.package,
      isDropOff,
      purposeOfShipment: rawPurpose || 'FOR_SALE',
      ...(formattedDutyCharge ? { dutyCharge: formattedDutyCharge } : {}),
      incoterm: shipment.package?.incoterm || 'FOB',
      freight: Number(shipment.package?.freight || 0),
      freightInsurance: Number(shipment.package?.freightInsurance || 0),
      iorEoriNumber: shipment.package?.iorEoriNumber ? String(shipment.package.iorEoriNumber).trim() : '',
      ...(pickupDate && timeSlot ? { pickupDate, timeSlot } : {}),
    };
    shipment.package = packageWithNewFields;
    const rawBulkIoss = shipment.package?.iossNumber
      ? String(shipment.package.iossNumber).trim()
      : (shipment as any)?.iossNumber
      ? String((shipment as any).iossNumber).trim()
      : '';
    delete (shipment.package as any).iossNumber;
    payload.push({
      ...shipment,
      purposeOfShipment: rawPurpose || 'FOR_SALE',
      extraDetails: {
        ...((shipment as any)?.extraDetails || {}),
        other: {
          ...((shipment as any)?.extraDetails?.other || {}),
          purposeOfShipment: rawPurpose || 'FOR_SALE',
        },
      },
      iossNumber: rawBulkIoss,
      shipper: getAddressObj(shipment.shipper),
      consignee: (() => {
        const isShipmentDoc = shipment.package?.isDocument === true || (shipment as any)?.type === 'document';
        const isShipmentB2B = ((shipment as any)?.shipmentMode || '').toLowerCase() === 'b2b';
        const isShipmentDestEU = isEUCountry(
          ((shipment.consignee?.country as any)?.name || shipment.consignee?.country) as string,
        );
        const isComplianceActive = isComplianceSubdomain();
        const isVatVisible = isComplianceActive && !isShipmentDoc && isShipmentB2B && isShipmentDestEU;
        const rawVat = String(shipment.consignee?.vat || (shipment.package as any)?.recipientVatTaxId || '').trim();
        const resConsignee = getAddressObj(shipment.consignee);
        if (isVatVisible && rawVat) {
          return {
            ...resConsignee,
            vat: rawVat,
          };
        }
        delete (resConsignee as any).vat;
        return resConsignee;
      })(),
      warehouseId,
      warehouseAddress,
      ...(formattedDutyCharge ? { dutyCharge: formattedDutyCharge } : {}),
    });
  });
  return payload;
}

export function getUserDetailsStateObj(data: ShipperConsigneeIdType) {
  const { phone, alternatePhone, name, companyName, line1, line2, city, state, country, pincode, email } = data;
  const temp: userDetailsType = {
    name,
    companyName: companyName ? companyName : '',
    line1,
    line2: line2 ? line2 : '',
    city,
    state,
    country,
    pincode,
    phoneNumber: phone.number,
    telephoneCode: phone.telephoneCode,
    altContactTelephoneCode: alternatePhone?.telephoneCode ?? '',
    altContactNo: alternatePhone?.number ?? '',
    email,
    altContactName: alternatePhone?.name ?? '',
    vat: (data as any)?.vat || (data as any)?.recipientVatTaxId || '',
    type: (data as any)?.type || '',
    IdType: (data as any)?.IdType || '',
    IdNumber: (data as any)?.IdNumber || '',
  };
  return temp;
}

export function getBoxStateObj(box: BoxType[] | any[]): any[] {
  if (!Array.isArray(box) || !box.length) {
    return [];
  }

  return box.map((b: any, ind: number) => {
    const rawProducts = Array.isArray(b.products) ? b.products : [];
    const products: any[] = rawProducts.map((p: any, pIdx: number) => {
      const rawCpsc = p.cpscCompliance || p.compliance?.us || p.cpsc;
      const disclaimObj = p.disclaimMessageSet || rawCpsc?.disclaimMessageSet || {};
      const refObj = p.referenceMessageSet || rawCpsc?.referenceMessageSet || {};

      const beingSold = parseBoolean(rawCpsc?.beingSold ?? p.cpscBeingSold ?? p.isRegulated);
      const certificateFiled = parseBoolean(rawCpsc?.certificateFiled ?? p.cpscCertificateFiled ?? p.isRegistered);
      const disclaimCode = normalizeCpscDisclaimCode(
        rawCpsc?.disclaimCode ?? p.cpscDisclaimCode ?? disclaimObj?.disclaimCode ?? p.disclaimCode,
      );
      const intendedUseCode = (
        rawCpsc?.intendedUseCode ??
        p.cpscIntendedUseCode ??
        disclaimObj?.intendedUseCode ??
        p.intendedUseCode ??
        ''
      ).trim();
      const additionalContext = (
        rawCpsc?.additionalContext ??
        p.cpscAdditionalContext ??
        disclaimObj?.intendedUseDescription ??
        disclaimObj?.additionalContext ??
        p.additionalContext ??
        ''
      ).trim();
      const productIdRegistry = (
        rawCpsc?.productIdRegistry ??
        p.cpscProductIdRegistry ??
        refObj?.registryProductId ??
        p.productIdRegistry ??
        ''
      ).trim();
      const certificateVersionId = (
        rawCpsc?.certificateVersionId ??
        p.cpscCertificateVersionId ??
        refObj?.productVersion ??
        p.certificateVersionId ??
        ''
      ).trim();
      const certifierId = (
        rawCpsc?.certifierId ??
        p.cpscCertifierId ??
        refObj?.certifierId ??
        p.certifierId ??
        ''
      ).trim();
      const cpscProductId = (rawCpsc?.productId ?? p.cpscProductId ?? p.productId ?? '').trim();
      const cpscProductIdType = (rawCpsc?.productIdType ?? p.cpscProductIdType ?? p.productIdType ?? 'SKU').trim();

      let cpscCompliance: UsCpscComplianceType | undefined = undefined;
      const hasAnyCpscData =
        beingSold !== undefined ||
        certificateFiled !== undefined ||
        Boolean(
          disclaimCode ||
            intendedUseCode ||
            additionalContext ||
            productIdRegistry ||
            certificateVersionId ||
            certifierId,
        );

      if (hasAnyCpscData) {
        cpscCompliance = {
          beingSold,
          certificateFiled,
          disclaimCode,
          intendedUseCode,
          additionalContext,
          productIdRegistry,
          certificateVersionId,
          certifierId,
          productId: cpscProductId,
          productIdType: cpscProductIdType,
        };
      }

      const rawQty = p.quantity ?? p.qty ?? p.Quantity ?? p.Qty;
      const quantity = rawQty !== undefined && rawQty !== null && rawQty !== '' ? Number(rawQty) : null;

      const rawUnitVal = p.unitValue ?? p.value ?? p.UnitValue ?? p.Value;
      const unitValue =
        rawUnitVal !== undefined && rawUnitVal !== null && rawUnitVal !== '' ? Number(rawUnitVal) : null;

      const totalValue =
        quantity !== null && unitValue !== null
          ? quantity * unitValue
          : p.totalValue !== undefined && p.totalValue !== null
          ? Number(p.totalValue)
          : 0;

      const description = p.description || p.Description || '';
      const HSNName = p.HSNName || p.hsnName || p.hsn_name || '';
      const HSNCode = p.HSNCode
        ? String(p.HSNCode)
        : p.hsnCode
        ? String(p.hsnCode)
        : p.hsn_code
        ? String(p.hsn_code)
        : '';

      const productId = p.productId
        ? String(p.productId).trim()
        : p.compliance?.eu?.productId
        ? String(p.compliance.eu.productId).trim()
        : p.sku
        ? String(p.sku).trim()
        : '';

      const productIdType = p.productIdType
        ? String(p.productIdType).trim()
        : p.compliance?.eu?.productIdType
        ? String(p.compliance.eu.productIdType).trim()
        : 'SKU';

      const merchantProductId = p.merchantProductId
        ? String(p.merchantProductId).trim()
        : p.compliance?.eu?.merchantProductId
        ? String(p.compliance.eu.merchantProductId).trim()
        : '';

      const standardManufacturerProductId =
        p.standardManufacturerProductId !== undefined && p.standardManufacturerProductId !== null
          ? String(p.standardManufacturerProductId).trim()
          : p.compliance?.eu?.standardManufacturerProductId !== undefined &&
            p.compliance?.eu?.standardManufacturerProductId !== null
          ? String(p.compliance.eu.standardManufacturerProductId).trim()
          : '';

      const nonStandardManufacturerProductId = p.nonStandardManufacturerProductId
        ? String(p.nonStandardManufacturerProductId).trim()
        : p.compliance?.eu?.nonStandardManufacturerProductId
        ? String(p.compliance.eu.nonStandardManufacturerProductId).trim()
        : '';

      const hydratedProduct: any = {
        ...p,
        id: pIdx.toString(),
        _id: p._id || p.id || pIdx.toString(),
        description,
        quantity,
        unitValue,
        totalValue,
        HSNName,
        HSNCode,
        _product: p._product ?? null,
        productId,
        productIdType,
        merchantProductId,
        standardManufacturerProductId,
        nonStandardManufacturerProductId,
        actions: null,
        ...(cpscCompliance ? { cpscCompliance } : {}),
      };
      delete hydratedProduct.isRegulated;
      delete hydratedProduct.isRegistered;
      delete hydratedProduct.disclaimMessageSet;
      delete hydratedProduct.referenceMessageSet;
      return hydratedProduct;
    });

    return {
      ...b,
      id: ind.toString(),
      length: b.length !== undefined && b.length !== null ? b.length : '',
      width: b.width !== undefined && b.width !== null ? b.width : '',
      height: b.height !== undefined && b.height !== null ? b.height : '',
      weight: b.weight !== undefined && b.weight !== null ? b.weight : '',
      boxCopyCount: b.boxCopyCount !== undefined && b.boxCopyCount !== null ? Number(b.boxCopyCount) : 0,
      selected: ind === 0,
      products,
    };
  });
}

export function getPackageStateObj(data: ShipmentByIdType['_packageId'], defaultCurrency: string) {
  const {
    unitOfMeasurement,
    packaging,
    insurance,
    isDocument,
    isCod,
    isDropOff,
    pickupDate,
    timeSlot,
    codAmount,
    currency,
    box,
    userReferenceNumber,
    invoiceNumber,
  } = data;
  const temp: PackageType = {
    unitOfMeasurement,
    packaging,
    insurance,
    isDocument,
    isCod,
    isDropOff,
    pickupDate: pickupDate ? dayjs(pickupDate) : null,
    timeSlot,
    codAmount,
    currency: defaultCurrency || currency,
    box: [...getBoxStateObj(box)],
    totalBoxWeight: 'Need calc',
    invoiceNumber: invoiceNumber || '',
    userReferenceNumber: userReferenceNumber || '',
  };
  return temp;
}

export function getPickupStateObj(data: ShipmentByIdType['_packageId']) {
  const { isDropOff, pickupDate, timeSlot } = data;
  const temp: PickupDataType = {
    type: isDropOff ? 'dropOff' : 'pickUp',
    date: isDropOff ? undefined : dayjs(pickupDate),
    timeSlot: isDropOff ? '' : timeSlot,
  };
  return temp;
}

export function getShipmentTypeStateObj(data: ShipmentByIdType, currency: string) {
  const anyData = data as any;
  const temp: ShipmentDataType = {
    numberOfBoxes: data._packageId.box.length.toString(),
    shipmentMode: data.shipmentMode,
    currency: currency || data._packageId.currency,
    type: data._packageId.isDocument ? 'document' : 'parcel',
    unit: data._packageId.unitOfMeasurement,
    invoiceNumber: '',
    referenceNumber: '',
    dutyCharge:
      (data as any)?.dutyCharge || (data as any)._packageId?.dutyCharge || (data as any).package?.dutyCharge || '',
    incoterm: anyData?.incoterm || anyData?._packageId?.incoterm || anyData?.extraDetails?.other?.incoterm || 'FOB',
    freight: anyData?.freight ?? anyData?._packageId?.freight ?? anyData?.extraDetails?.other?.freight ?? 0,
    freightInsurance:
      anyData?.freightInsurance ??
      anyData?._packageId?.freightInsurance ??
      anyData?.extraDetails?.other?.freightInsurance ??
      0,
    iossNumber:
      anyData?.iossNumber || anyData?._packageId?.iossNumber || anyData?.extraDetails?.other?.iossNumber || '',
    recipientVatTaxId:
      anyData?.consignee?.vat ||
      anyData?._consigneeId?.vat ||
      anyData?.recipientVatTaxId ||
      anyData?._packageId?.recipientVatTaxId ||
      anyData?.extraDetails?.other?.recipientVatTaxId ||
      '',
    iorEoriNumber:
      anyData?.iorEoriNumber ||
      anyData?._packageId?.iorEoriNumber ||
      anyData?.extraDetails?.other?.ior ||
      anyData?.extraDetails?.other?.iorEoriNumber ||
      '',
  };
  return temp;
}
